import { useTranslation } from 'react-i18next';
import {
  useState, useEffect, MutableRefObject, useCallback,
} from 'react';
import {
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  gridExpandedSortedRowIdsSelector,
  gridVisibleColumnDefinitionsSelector,
  GridApiPro, GridSortModel, getGridStringOperators, GridFilterOperator, gridStringOrNumberComparator,
} from '@mui/x-data-grid-pro';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';

export function useTable(apiRef: MutableRefObject<GridApiPro>) {
  const { t } = useTranslation(['common', 'team']);
  const [exporting, setExporting] = useState(false);

  const filterOperators = getGridStringOperators();
  const filterOperatorsLookup = filterOperators.reduce<
  Record<string, GridFilterOperator>
  >((acc, operator) => {
    acc[operator.value] = operator;
    return acc;
  }, {});

  const [_, setCoordinates] = useState({
    rowIndex: 3,
    colIndex: 3,
  });

  const scrollByCords = useCallback((cords: {
    rowIndex: number, colIndex: number
  }) => {
    const { rowIndex, colIndex } = cords;

    apiRef.current?.scrollToIndexes(cords);
    const id = gridExpandedSortedRowIdsSelector(apiRef)[rowIndex];
    const column = gridVisibleColumnDefinitionsSelector(apiRef)[colIndex];
    apiRef.current?.setCellFocus(id, column.field);
  }, [apiRef]);

  const handleScrollTable = (position: string) => {
    const maxColIndex = gridVisibleColumnDefinitionsSelector(apiRef).length - 1;

    setCoordinates((coords) => {
      switch (position) {
        case 'left': {
          const cords = { ...coords, colIndex: Math.max(0, coords.colIndex - 1) };
          scrollByCords(cords);
          return cords;
        }
        case 'right': {
          const cords = { ...coords, colIndex: Math.min(maxColIndex, coords.colIndex + 1) };
          scrollByCords(cords);
          return cords;
        }
        default: {
          const cords = { ...coords, rowIndex: 0, colIndex: 0 };
          scrollByCords(cords);
          return cords;
        }
      }
    });
  };

  const localText = {
    toolbarExport: t('export'),
    toolbarDensityCompact: t('compact'),
    toolbarDensityStandard: t('standard'),
    toolbarDensity: t('density'),
    toolbarDensityComfortable: t('comfortable'),
    toolbarColumns: t('columns'),
    columnsPanelShowAllButton: t('showAll'),
    columnsPanelHideAllButton: t('hideAll'),
    columnsPanelTextFieldPlaceholder: t('columnTitle'),
    columnsPanelTextFieldLabel: t('findColumn'),
    toolbarExportPrint: t('print'),
    toolbarExportCSV: t('downloadCsv'),
    columnMenuFilter: t('filter'),
    columnMenuHideColumn: t('hideColumn'),
    columnMenuManageColumns: t('manageColumns'),
    columnMenuSortAsc: t('sortByASC'),
    columnMenuSortDesc: t('sortByDESC'),
    filterPanelRemoveAll: t('removeAll', { ns: 'team' }),
    filterPanelColumns: t('columns'),
    filterPanelOperator: t('operator'),
    filterPanelAddFilter: t('addFilter'),
    filterPanelInputLabel: t('value'),
    filterPanelInputPlaceholder: t('filterValue'),
    filterOperatorContains: t('contains'),
    filterOperatorDoesNotContain: t('doesNotContains'),
    filterOperatorEquals: t('equals'),
    filterOperatorDoesNotEqual: t('doesNotEquals'),
    filterOperatorStartsWith: t('startsWith'),
    filterOperatorEndsWith: t('endsWith'),
    filterOperatorIsEmpty: t('isEmpty'),
    filterOperatorIsNotEmpty: t('isNotEmpty'),
    filterOperatorIsAnyOf: t('isAnyOf'),
  };

  const sortRows = (rows: any, sortModel: GridSortModel) => {
    let data = [...rows];
    if (sortModel && sortModel.length > 0) {
      sortModel.forEach(({ field, sort }) => {
        if (field && sort) {
          data = data?.sort((a: any, b: any) => (
            gridStringOrNumberComparator(a[field], b[field], {} as any, {} as any)
            * (sort === 'asc' ? 1 : -1)
          ));
        }
      });

      return data;
    }

    return data;
  };

  const filterRows = (rows: any, filterModel: GridFilterModel) => {
    let data = [...rows];
    const method = filterModel.logicOperator === 'or' ? 'some' : 'every';

    data = data.filter((row) => filterModel.items[method]((item) => {
      const filter = filterOperatorsLookup[item.operator];
      if (!filter) {
        return true;
      }
      if (!filter.requiresFilterValue && !item.value) {
        return true;
      }
      const colDef = {} as any;
      const apiRef = {} as any;
      return filter.getApplyFilterFn(item, colDef)?.(
        row[item.field],
        row,
        colDef,
        apiRef,
      );
    }));

    return data;
  };

  const handleExport = (fileName: string) => {
    setExporting(true);

    setTimeout(() => {
      apiRef.current.exportDataAsCsv({
        fileName,
      });

      setTimeout(() => {
        setExporting(false);
      }, 500);
    }, 500);
  };

  return {
    localText,
    handleScrollTable,
    sortRows,
    filterRows,
    handleExport,
    exporting,
  };
}

export function useEditTable<T extends { id: string | number, isNew: boolean }>(tableRow: Array<T>, isPayroll = false) {
  const [rows, setRows] = useState(tableRow);

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  useEffect(() => {
    if (isPayroll) setRows(tableRow);
  }, [tableRow]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // eslint-disable-next-line no-param-reassign
      event.defaultMuiPrevented = true;
    }
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  return {
    rowModesModel,
    handleRowEditStop,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    rows,
    setRows,
    setRowModesModel,
    handleEditClick,
    handleDeleteClick,
  };
}
